import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  Button,
  message,
  Row,
  Col,
  Typography,
  Dropdown,
  Menu,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  Switch,
} from "antd";
import ListTable from "../../../common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import {
  getData,
  postData,
  deleteData,
  putData,
} from "../../../apiServices/common";
import {
  CARTON_FLOOR_LIST_ENDPOINT,
  CARTON_FLOOR_ADD_ENDPOINT,
  CARTON_FLOOR_EDIT_ENDPOINT,
  CARTON_FLOOR_DELETE_ENDPOINT,
  CARTON_WAREHOUSE_LIST_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  CARTON_FLOOR_CREATE_PERMISSION,
  CARTON_FLOOR_EDIT_PERMISSION,
  CARTON_FLOOR_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const CartonFloorConfig = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [warehouseList, setWarehouseList] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const [cartonFloorForm] = Form.useForm();
  const { Option } = Select;
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllCartonFloor = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${CARTON_FLOOR_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name: name }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setFetchError("Could not load this list.");
        message.error("Error getting carton floor data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllCartonFloor(filterData);
    getAllCartonWarehouse();
  }, [refresh, filterData, getAllCartonFloor]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    cartonFloorForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const getAllCartonWarehouse = async (filterValues) => {
    setLoading(true);

    const query = `${CARTON_WAREHOUSE_LIST_ENDPOINT}`;
    const payload = {
      page: 1,
      per_page: 100,
      ...filterValues,
    };

    const response = await getData(query, false, payload);
    if (response?.status === 200) {
      setLoading(false);
      setWarehouseList(response?.data?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting carton warehouse data");
    }
  };

  const deleteSelectedCartonFloor = async (id) => {
    setLoading(true);
    const response = await deleteData(`${CARTON_FLOOR_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Carton Floor Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error(response?.message || "Something went wrong");
    }
  };

  const editSelectedCartonFloor = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status ? "1" : "0",
      carton_warehouse_id: Number(formValues?.carton_warehouse_id),
    };

    const response = await putData(
      `${CARTON_FLOOR_EDIT_ENDPOINT}/${formValues?.id}`,
      bodyData,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Carton Floor Edited Successfully");
      refetch();
      cartonFloorForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewCartonFloor = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status ? "1" : "0",
      carton_warehouse_id: Number(formValues?.carton_warehouse_id),
    };

    const response = await postData(CARTON_FLOOR_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Carton Floor Created Successfully");
      refetch();
      cartonFloorForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);

    // Push the warehouse info to the warehouse list if it isn't already present
    const warehouseInfo = {
      id: Number(record?.warehouse_info?.id),
      name: record?.warehouse_info?.name,
    };
    if (warehouseList && !warehouseList.some((item) => item.id === warehouseInfo.id)) {
      const warehouseListCopy = [...warehouseList];
      warehouseListCopy.push(warehouseInfo);
      setWarehouseList(warehouseListCopy);
    }

    cartonFloorForm.setFieldsValue({
      id: record?.id,
      name: record?.name,
      description: record?.description,
      status: record?.status === "1" ? true : false,
      carton_warehouse_id: record?.warehouse_info?.id,
    });
    setIsModalOpen(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Carton Floor Name",
      dataIndex: ["name"],
      key: "carton_floor_name",
    },
    {
      title: "Carton Warehouse",
      dataIndex: ["warehouse_info", "name"],
      key: "carton_warehouse_name",
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "carton_floor_status",
      render: (_, record) => {
        return <div>{record?.status === "1" ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Description",
      dataIndex: ["description"],
      key: "carton_floor_description",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedCartonFloor(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([CARTON_FLOOR_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openModalForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([CARTON_FLOOR_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllCartonFloor}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllCartonFloor],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([CARTON_FLOOR_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([CARTON_FLOOR_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Carton Floor Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllCartonFloor(filterData)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Carton Floor" : "Create Carton Floor"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form
          form={cartonFloorForm}
          layout="vertical"
          onFinish={isEdit ? editSelectedCartonFloor : createNewCartonFloor}
        >
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Carton Floor Name"
                label="Carton Floor Name"
                rules={[
                  {
                    required: true,
                    message: "Carton Floor Name Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                placeholder="Description"
                label="Description"
                rules={[
                  {
                    required: true,
                    message: "Description Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="carton_warehouse_id"
                placeholder="Carton Warehouse"
                label="Carton Warehouse"
                rules={[
                  {
                    required: true,
                    message: "Carton Warehouse Required",
                  },
                ]}
              >
                <Select
                  placeholder="Select Carton Warehouse"
                  showSearch
                  style={{ width: "100%" }}
                  onSearch={(value) => getAllCartonWarehouse({ name: value })}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {warehouseList?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CartonFloorConfig;
